// src/shared/protocol/serialize.ts
// Converts between GameState (with Map fields) and SerializedGameState (JSON-safe wire format).
import type { GameState } from '@shared/game';
import { normalizeDroppedKeyPiles } from '@shared/game';
import type { SerializedGameState } from './messages';

/**
 * Serialize a GameState to a JSON-safe wire object.
 * Map fields are converted to entry-array tuples; all other fields are copied as-is.
 * Uses explicit field-by-field construction (no structuredClone of the full state) for clarity.
 */
export function serializeGameState(state: GameState): SerializedGameState {
  return {
    phase: state.phase,
    round: state.round,
    maxRounds: state.maxRounds,
    currentPlayerIndex: state.currentPlayerIndex,
    turnOrder: state.turnOrder,
    players: Array.from(state.players.entries()),
    boardNodeIds: state.boardNodeIds,
    treasureChestNodeId: state.treasureChestNodeId,
    settings: state.settings,
    eventLog: state.eventLog,
    rngSeed: state.rngSeed,
    rngState: state.rngState,
    weaponsCacheNodeId: state.weaponsCacheNodeId,
    weaponsCacheRespawnIn: state.weaponsCacheRespawnIn,
    stats: Array.from(state.stats.entries()),
    boardModifiers: state.boardModifiers,
    droppedKeys: Array.from(state.droppedKeys.entries()).map(([nodeId, piles]) => [
      nodeId,
      normalizeDroppedKeyPiles(piles),
    ]),
    lastMoveByPlayer: Array.from(state.lastMoveByPlayer.entries()),
    activeClouds: state.activeClouds,
    npcs: state.npcs,
    activeCursedRelics: state.activeCursedRelics,
    pendingOrbitalCannon: state.pendingOrbitalCannon,
    mysteryBoxTraps: state.mysteryBoxTraps,
    pendingMysteryBoxChoice: state.pendingMysteryBoxChoice,
    pendingSoulDuel: state.pendingSoulDuel,
    soulAnchorClaimModes: Array.from((state.soulAnchorClaimModes ?? new Map()).entries()),
    pendingSoulAnchorClaim: state.pendingSoulAnchorClaim ?? null,
    pendingBoardEncounter: state.pendingBoardEncounter ?? null,
    pendingRelocationPrompts: structuredClone(state.pendingRelocationPrompts ?? []),
    pendingForkChoice: state.pendingForkChoice ?? null,
    endGame: state.endGame ?? null,
    awardsApplied: state.awardsApplied ?? false,
    turnActionDeadlineAtMs: state.turnActionDeadlineAtMs ?? null,
    forkChoiceDeadlineAtMs: state.forkChoiceDeadlineAtMs ?? null,
    pause: state.pause ?? null,
  };
}

/**
 * Deserialize a wire object back to a GameState.
 * Reconstructs Map fields from their entry-array tuples.
 * Non-Map array fields are deep-cloned via structuredClone to avoid shared mutable state.
 */
export function deserializeGameState(wire: SerializedGameState): GameState {
  return {
    phase: wire.phase,
    round: wire.round,
    maxRounds: wire.maxRounds,
    currentPlayerIndex: wire.currentPlayerIndex,
    turnOrder: structuredClone(wire.turnOrder),
    players: new Map(wire.players),
    boardNodeIds: structuredClone(wire.boardNodeIds),
    treasureChestNodeId: wire.treasureChestNodeId,
    settings: structuredClone(wire.settings),
    eventLog: structuredClone(wire.eventLog),
    rngSeed: wire.rngSeed,
    rngState: wire.rngState,
    weaponsCacheNodeId: wire.weaponsCacheNodeId,
    weaponsCacheRespawnIn: wire.weaponsCacheRespawnIn,
    stats: new Map(wire.stats),
    boardModifiers: structuredClone(wire.boardModifiers),
    droppedKeys: new Map(
      wire.droppedKeys.map(([nodeId, piles]) => [nodeId, normalizeDroppedKeyPiles(piles)]),
    ),
    lastMoveByPlayer: new Map(wire.lastMoveByPlayer),
    activeClouds: structuredClone(wire.activeClouds),
    npcs: structuredClone(wire.npcs),
    activeCursedRelics: structuredClone(wire.activeCursedRelics),
    pendingOrbitalCannon: wire.pendingOrbitalCannon,
    mysteryBoxTraps: structuredClone(wire.mysteryBoxTraps),
    pendingMysteryBoxChoice: wire.pendingMysteryBoxChoice,
    pendingSoulDuel: wire.pendingSoulDuel,
    soulAnchorClaimModes: new Map(wire.soulAnchorClaimModes ?? []),
    pendingSoulAnchorClaim: wire.pendingSoulAnchorClaim ?? null,
    pendingBoardEncounter: wire.pendingBoardEncounter ?? null,
    pendingRelocationPrompts: structuredClone(wire.pendingRelocationPrompts ?? []),
    pendingForkChoice: wire.pendingForkChoice ?? null,
    endGame: wire.endGame ?? null,
    awardsApplied: wire.awardsApplied ?? false,
    turnActionDeadlineAtMs: wire.turnActionDeadlineAtMs ?? null,
    forkChoiceDeadlineAtMs: wire.forkChoiceDeadlineAtMs ?? null,
    pause: wire.pause ?? null,
  };
}
